// PettoBook POS — 收銀 · 紀錄 · 商品 · 庫存 · 報表 · 設定.
// One money path: every sale is checkout_sale(), every refund refund_sale(),
// every stock change adjust_stock(). The appointment share of a sale is still
// recorded through record_payment_atomic inside checkout_sale, so the
// calendar, 收款 history, loyalty-on-done and existing reports stay correct.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ShoppingBag, Receipt, Package, Warehouse, BarChart3, Settings2, Vault, Truck, BadgePercent } from "lucide-react";
import { C, Card, Label, Field, Empty, PosStyles, CameraScanner, Sheet, beep, money } from "./posUi.jsx";
import { newId, buildSalePayload, ticketTotals, lineTotal, heldStale, posErrorText } from "./posMath.js";
import { printReceipt } from "./posReceipt.js";
import Register from "./Register.jsx";
import PaySheet from "./PaySheet.jsx";
import History from "./History.jsx";
import Products, { ToggleRow } from "./Products.jsx";
import Inventory from "./Inventory.jsx";
import Reports from "./Reports.jsx";
import Shift from "./Shift.jsx";
import Taxonomy from "./Taxonomy.jsx";
import Purchasing from "./Purchasing.jsx";
import Promotions from "./Promotions.jsx";
import { posTierOf, posAllows, POS_TAB_NEED, posTierPlan, POS_DEFAULT_MAX, zhEn, dateLocale } from "./posTier.js";
import { startBarcodeListener } from "../../lib/posScanner.js";
import { outboxList, outboxAdd, outboxSync, outboxRemove, outboxRetry, canQueue, isNetworkError } from "./posOutbox.js";
import {
  fetchPosProducts, fetchHeldTickets, saveHeldTicket, deleteHeldTicket,
  checkoutSale, fetchAppointmentsByIds, fetchMembership, fetchOpenShift,
  fetchCategories, fetchBrands, fetchSuppliers, fetchPromotions, fetchPosPlanInfo, fetchPosProductsSince,
} from "../../lib/api.js";

const TABS = [
  ["register", zhEn("收銀", "POS"), ShoppingBag], ["history", zhEn("紀錄", "History"), Receipt], ["products", zhEn("商品", "Products"), Package],
  ["inventory", zhEn("庫存", "Stock"), Warehouse], ["purchasing", zhEn("採購", "Purchasing"), Truck], ["promotions", zhEn("優惠", "Promotions"), BadgePercent], ["shift", zhEn("交更", "Shift"), Vault], ["reports", zhEn("報表", "Reports"), BarChart3], ["settings", zhEn("設定", "Settings"), Settings2],
];
const emptyTicket = () => ({ saleId: newId(), heldId: null, customerId: null, lines: [], orderDiscount: 0, note: "" });
const today = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };

export default function PosApp({ state, setState, user, tenant, sym = "$", helpers, toast, plans, onUpgrade }) {
  const tid = tenant.id;
  // What the shop's plan includes (pos_plan_info is the authority; the
  // local guess covers the first render / offline).
  const [planInfo, setPlanInfo] = useState(null);
  const reloadPlanInfo = useCallback(() => fetchPosPlanInfo(tid).then(setPlanInfo).catch(() => {}), [tid]);
  useEffect(() => { reloadPlanInfo(); }, [reloadPlanInfo, tenant.plan, tenant.posEnabled]);
  const tier = planInfo?.tier || posTierOf(tenant, plans);
  const has = useCallback((need) => posAllows(tier, need), [tier]);
  const maxProducts = planInfo ? planInfo.maxProducts : POS_DEFAULT_MAX[tier] ?? null;
  const [tab, setTab] = useState(() => { try { return localStorage.getItem("petos:pos:tab") || "register"; } catch { return "register"; } });
  const [products, setProducts] = useState(null);
  const [held, setHeld] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [promotions, setPromotions] = useState([]);
  // Import / typed names can create categories + brands server-side.
  const reloadTaxonomy = useCallback(() => { fetchCategories(tid).then(setCategories).catch(() => {}); fetchBrands(tid).then(setBrands).catch(() => {}); }, [tid]);
  const reloadPromotions = useCallback(() => fetchPromotions(tid).then(setPromotions).catch(() => {}), [tid]);
  useEffect(() => { reloadPromotions(); }, [reloadPromotions]);
  const [productsView, setProductsView] = useState("list");   // list | category | brand
  const [loadErr, setLoadErr] = useState("");
  const [payTotals, setPayTotals] = useState(null);
  const [scanner, setScanner] = useState(null);       // {cb, title, continuous, status}
  const [stockFocus, setStockFocus] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const ticketKey = "petos:pos:ticket:" + tid;
  const [ticket, setTicketRaw] = useState(() => {
    try { const t = JSON.parse(localStorage.getItem(ticketKey) || "null"); if (t && Array.isArray(t.lines)) return { ...emptyTicket(), ...t }; } catch { /* none */ }
    return emptyTicket();
  });
  const setTicket = useCallback((v) => setTicketRaw(t => {
    const next = v === null ? emptyTicket() : typeof v === "function" ? (v(t) || emptyTicket()) : v;
    try { localStorage.setItem(ticketKey, JSON.stringify(next)); } catch { /* private mode */ }
    return next;
  }), [ticketKey]);
  useEffect(() => { try { localStorage.setItem("petos:pos:tab", tab); } catch { /* */ } }, [tab]);
  // Back on 收銀 → fresh promotions (edited on another till / used up elsewhere).
  useEffect(() => { if (tab === "register") reloadPromotions?.(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const [online, setOnline] = useState(() => typeof navigator === "undefined" ? true : navigator.onLine !== false);
  const lastSyncRef = useRef(null);
  const load = useCallback(async () => {
    setLoadErr("");
    try {
      const [ps, hs, cs, bs, ss] = await Promise.all([fetchPosProducts(tid), fetchHeldTickets(tid),
        fetchCategories(tid).catch(() => []), fetchBrands(tid).catch(() => []), fetchSuppliers(tid).catch(() => [])]);
      setProducts(ps); setHeld(hs); setCategories(cs); setBrands(bs); setSuppliers(ss);
      lastSyncRef.current = new Date(Date.now() - 120000).toISOString();
      // Offline copy of the catalogue (and its categories) for a till that
      // reloads with no connection. Skipped if it won't fit.
      try { localStorage.setItem("petos:pos:cache:" + tid, JSON.stringify({ at: Date.now(), ps, cs })); } catch { /* too big / private mode */ }
    } catch (e) {
      let cached = null;
      try { cached = JSON.parse(localStorage.getItem("petos:pos:cache:" + tid) || "null"); } catch { /* none */ }
      if (cached?.ps?.length && isNetworkError(e)) {
        setProducts(p => p && p.length ? p : cached.ps); setCategories(c => c && c.length ? c : (cached.cs || []));
        setOnline(false);
      } else { setLoadErr(e?.message || String(e)); setProducts(p => p || []); }
    }
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  // ── data from the app ──
  const customers = useMemo(() => (tenant.customerIds || []).map(id => state.users?.[id]).filter(u => u && u.enabled !== false)
    .map(u => ({ id: u.id, name: u.name || "", phone: u.phone || "", wallet: Number(u.wallet) || 0, points: Number(u.points) || 0 })), [tenant.customerIds, state.users]);
  const custMap = useMemo(() => Object.fromEntries(customers.map(c => [c.id, c])), [customers]);
  const customerById = useCallback((id) => id ? (custMap[id] || (state.users?.[id] ? { id, name: state.users[id].name, phone: state.users[id].phone, wallet: Number(state.users[id].wallet) || 0, points: Number(state.users[id].points) || 0 } : null)) : null, [custMap, state.users]);
  const petOf = useCallback((id) => id ? state.pets?.[id] || null : null, [state.pets]);
  const aptDue = useCallback((a) => helpers.aptRemainingBalance(a), [helpers]);
  const aptById = useCallback((id) => (state.appointments || []).find(x => x.id === id) || null, [state.appointments]);
  const openApts = useMemo(() => (state.appointments || []).filter(a => a.tenantId === tid && (a.kind || "grooming") !== "event" &&
    !["cancelled", "noshow"].includes(a.status) && !a.paid && !a.deletedAt && aptDue(a) > 0), [state.appointments, tid, aptDue]);
  // Unpaid visits from the last 14 days (and upcoming) by default; older
  // ones are usually stale bookings nobody closed, so they sit behind
  // 「逾期未付」 instead of crowding the till.
  const aptsFor = useCallback((cid, includeOld = false) => {
    const d = new Date(); d.setDate(d.getDate() - 14);
    const cutoff = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
    return openApts.filter(a => a.customerId === cid && (includeOld || String(a.date) >= cutoff))
      .sort((x, y) => (y.date === today()) - (x.date === today()) || String(y.date).localeCompare(String(x.date))).slice(0, includeOld ? 30 : 8);
  }, [openApts]);
  const todayUnpaid = useMemo(() => openApts.filter(a => a.date === today()).sort((x, y) => String(x.time).localeCompare(String(y.time))), [openApts]);
  const spas = useMemo(() => (tenant.services || []).filter(s => s.kind === "spa" && s.enabled !== false), [tenant.services]);
  const addons = useMemo(() => (tenant.addons || []).filter(a => a.enabled !== false && !String(a.name || "").startsWith("💆")), [tenant.addons]);
  const priceSpa = useCallback((svc, pet) => pet ? Number(helpers.getPricingDetail(pet, svc, tenant)?.total) || 0 : Number(svc.basePrice) || 0, [helpers, tenant]);
  const priceAddon = useCallback((a, pet) => Number(helpers.computeAddonPrice(a, pet)?.price) || 0, [helpers]);
  const methods = useMemo(() => helpers.checkoutMethodsOf(tenant).filter(m => !helpers.isCashMethod(m)), [helpers, tenant]);

  // Staff permissions (owner / admin always yes). Defaults mirror pos_staff_can.
  const STAFF_DEFAULTS = { refund: false, discount: true, priceOverride: false, closeShift: true };
  const isBoss = user.role === "owner" || user.role === "admin" || user.isSuper;
  // topup / reports follow the staff member's own 權限 (顧客資料 / 營收),
  // the same switches the rest of the app uses; deleting products is the owner's.
  const can = useCallback((key) => isBoss || (
    key === "topup" ? !!user.permissions?.manageCRM
    : key === "reports" ? !!user.permissions?.viewRevenue
    : key === "deleteProduct" ? false
    : (tenant.posSettings?.staffCan?.[key] ?? STAFF_DEFAULTS[key])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBoss, tenant.posSettings, user.permissions]);
  // A tab remembered from the owner on a shared till.
  useEffect(() => { if (tab === "reports" && !can("reports")) setTab("register"); }, [tab, can]);
  const [shiftOpen, setShiftOpen] = useState(undefined);   // undefined = unknown, null = none open
  useEffect(() => { fetchOpenShift(tid).then(setShiftOpen).catch(() => setShiftOpen(undefined)); }, [tid]);
  // ── offline mode: queued sales + connection state ──
  const [outbox, setOutbox] = useState(() => outboxList(tid));
  const [outboxOpen, setOutboxOpen] = useState(false);
  const runSync = useCallback(async () => {
    if (!outboxList(tid).length) { setOutbox([]); return; }
    const r = await outboxSync(tid);
    setOutbox(r.list);
    // A send went through, so the connection is back even if the browser
    // never fired "online" (a dropped request rather than lost Wi-Fi).
    if (r.sent || !r.list.length) setOnline(navigator.onLine !== false);
    if (r.sent) {
      toast?.(zhEn("已上傳 ", "Uploaded ") + r.sent + zhEn(" 張離線單 ✓", " offline sales ✓"));
      setRefreshKey(k => k + 1);
      fetchPosProducts(tid).then(setProducts).catch(() => {});
    }
  }, [tid, toast]);
  useEffect(() => {
    const up = () => { setOnline(true); runSync(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    runSync();
    const iv = setInterval(() => { if (navigator.onLine !== false && outboxList(tid).length) runSync(); }, 30000);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); clearInterval(iv); };
  }, [tid, runSync]);

  // ── other devices: stock, 掛單, shift and promotions change on other tills
  // too. Every 45 s while the till is on screen (and whenever it comes back
  // to the front) pick up products changed since the last look, plus the
  // held tickets, the open shift and promotions. Cheap: only changed rows.
  useEffect(() => {
    const refresh = async () => {
      if (document.visibilityState === "hidden" || navigator.onLine === false || !lastSyncRef.current) return;
      const since = lastSyncRef.current, stamp = new Date(Date.now() - 120000).toISOString();
      try {
        const [changed, hs] = await Promise.all([fetchPosProductsSince(tid, since), fetchHeldTickets(tid)]);
        if (changed.length) {
          const byId = Object.fromEntries(changed.map(p => [p.id, p]));
          setProducts(ps => { const cur = ps || []; const seen = new Set(cur.map(p => p.id)); return [...cur.map(p => byId[p.id] || p), ...changed.filter(p => !seen.has(p.id))]; });
        }
        setHeld(hs);
        lastSyncRef.current = stamp;
        fetchOpenShift(tid).then(setShiftOpen).catch(() => {});
        reloadPromotions();
      } catch { /* next round */ }
    };
    const iv = setInterval(refresh, 45000);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis); window.addEventListener("focus", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, [tid, reloadPromotions]);

  const heldQty = useCallback((pid, exceptHeldId) => held.reduce((s, h) => s + (h.id === exceptHeldId || heldStale(h) ? 0 : Number(h.product_qty?.[pid]) || 0), 0), [held]);

  // SPA / add-on onto an appointment: same price maths as the 收款 sheet,
  // saved before checkout so checkout_sale prices the visit with it.
  const onAddToApt = useCallback(async (aptId, svc, kind) => {
    const apt = (state.appointments || []).find(a => a.id === aptId);
    if (!apt) return null;
    const pet = petOf(apt.petId);
    const spaIds = kind === "spa" ? [...new Set([...(apt.spaIds || []), svc.id])] : (apt.spaIds || []);
    const addonNames = kind === "addon" ? [...(apt.addons || []), svc.name] : (apt.addons || []);
    const addonPrice = spaIds.reduce((s, id) => { const sp = spas.find(x => x.id === id) || (tenant.services || []).find(x => x.id === id); return s + (sp ? priceSpa(sp, pet) : 0); }, 0)
      + addonNames.filter(n => !String(n).startsWith("💆")).reduce((s, n) => { const a = (tenant.addons || []).find(x => x.name === n); return s + (a ? priceAddon(a, pet) : 0); }, 0);
    const updated = { ...apt, spaIds, addons: addonNames, addonPrice };
    try {
      await helpers.saveAppointment(updated);
      setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === aptId ? updated : a) }));
      toast?.(zhEn("已加入「", "Added \"") + svc.name + zhEn("」到 ", "\" to ") + (pet?.name || zhEn("預約", "booking")));
      return updated;
    } catch (e) { toast?.(zhEn("加入失敗：", "Couldn't add: ") + (e?.message || e), "error"); return null; }
  }, [state.appointments, petOf, spas, tenant, priceSpa, priceAddon, helpers, setState, toast]);

  // 收銀台結帳 from an appointment: the app leaves its id in localStorage and
  // switches here. Put its balance on the ticket with the customer; a ticket
  // that belongs to someone else is held first so nothing is lost.
  const incomingDone = useRef(false);
  useEffect(() => {
    if (incomingDone.current || products === null) return;
    let id = null;
    try { id = localStorage.getItem("petos:pos:incoming"); localStorage.removeItem("petos:pos:incoming"); } catch { /* */ }
    incomingDone.current = true;
    if (!id) return;
    const a = (state.appointments || []).find(x => x.id === id);
    if (!a) return;
    setTab("register");
    const pet = petOf(a.petId);
    const line = { key: newId(), kind: "appointment", aptId: a.id, petId: a.petId, name: (pet?.name ? pet.name + " · " : "") + (a.service || zhEn("美容", "Grooming")), due: aptDue(a), date: a.date };
    const other = ticket.lines.length > 0 && ticket.customerId !== a.customerId;
    if (other) {
      const productQty = {};
      for (const l of ticket.lines) if (l.kind === "product") productQty[l.productId] = (productQty[l.productId] || 0) + l.qty;
      const label = (customerById(ticket.customerId)?.name || zhEn("散客", "Walk-in")) + " · " + new Date().toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" });
      saveHeldTicket({ id: ticket.heldId || newId(), tenantId: tid, customerId: ticket.customerId, label, ticket: { lines: ticket.lines, customerId: ticket.customerId, orderDiscount: ticket.orderDiscount, note: ticket.note }, productQty })
        .then(row => { setHeld(hs => [row, ...hs.filter(x => x.id !== row.id)]); toast?.(zhEn("之前張單已掛起", "The previous ticket was put on hold")); })
        .catch(() => toast?.(zhEn("之前張單掛起失敗", "Couldn't hold the previous ticket"), "error"));
      setTicket({ ...emptyTicket(), customerId: a.customerId, lines: [line] });
      return;
    }
    setTicket(t => t.lines.some(l => l.aptId === a.id) ? t : { ...t, customerId: a.customerId, lines: [...t.lines, line] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // Booking lines show what the booking owes NOW. The amount is copied onto
  // the ticket when the line is added and the ticket is kept in localStorage,
  // so a discount set afterwards (on the booking, or here) used to leave the
  // till showing the old price — and a second 收款 from the booking found the
  // line already there and kept it as it was.
  useEffect(() => {
    setTicket(t => {
      let changed = false;
      const lines = t.lines.map(l => {
        if (l.kind !== "appointment") return l;
        const a = aptById(l.aptId);
        if (!a) return l;
        const due = aptDue(a);
        if (due === l.due) return l;
        changed = true;
        return { ...l, due };
      });
      return changed ? { ...t, lines } : t;
    });
  }, [aptById, aptDue, setTicket]);

  // 折扣 / 改價 on a booking at the till. Written to the booking itself — the
  // same discount object + priceAdjustLog entry the booking screen writes — so
  // checkout_sale (which prices bookings from the row), reports and receipts
  // all agree on the amount.
  const setAptPrice = useCallback(async (aptId, { discount, basePrice }) => {
    const a = aptById(aptId);
    if (!a) return false;
    const d = new Date();
    const stamp = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0") + " " + String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0");
    const log = [...(a.priceAdjustLog || [])];
    const next = { ...a };
    if (basePrice != null && Number(basePrice) !== Number(a.basePrice)) {
      log.push({ field: "basePrice", from: a.basePrice, to: basePrice, reason: "收銀改價", by: user.id, at: stamp });
      next.basePrice = basePrice;
    }
    const newDisc = discount && Number(discount.amount) > 0 ? { type: discount.type, amount: Number(discount.amount), reason: discount.reason || "", at: stamp, by: user.id } : null;
    const oldDisc = JSON.stringify(a.discount || null), nd = JSON.stringify(newDisc);
    if (oldDisc !== nd) log.push({ field: "discount", from: oldDisc, to: nd, by: user.id, at: stamp });
    next.discount = newDisc;
    next.priceAdjustLog = log;
    try {
      await helpers.saveAppointment(next);
      setState(s => ({ ...s, appointments: s.appointments.map(x => x.id === aptId ? next : x) }));
      toast?.(zhEn("已更新預約收費", "Booking price updated"));
      return true;
    } catch (e) { toast?.(zhEn("更新失敗：", "Update failed: ") + (e?.message || e), "error"); return false; }
  }, [aptById, helpers, setState, toast, user.id]);

  // ── checkout ──
  const submitSale = async (tenders) => {
    const payload = buildSalePayload({ saleId: ticket.saleId, tenantId: tid, customerId: ticket.customerId, orderDiscount: ticket.orderDiscount, heldTicketId: ticket.heldId, note: ticket.note, lines: ticket.lines, payments: tenders });
    // No connection (or it dropped mid-sale): queue it and carry on. A sale
    // that reached the server before the reply was lost is matched on
    // resend by its id, so it is never recorded twice.
    const queue = () => {
      if (!canQueue(payload)) throw new Error(zhEn("離線時唔可以用錢包、積分或者儲值 — 請收現金或者卡，或者等連返網", "Wallet, points and top-ups can't be used offline — take cash or card, or wait until you're back online"));
      setOutbox(outboxAdd(tid, { saleId: payload.id, payload: { ...payload, offlineAt: new Date().toISOString() }, total: ticketTotals(ticket.lines, ticket.orderDiscount).total, customerName: customerById(ticket.customerId)?.name || "" }));
      setOnline(false);
      const change = tenders.reduce((x, t) => x + (t.tendered != null ? Math.max(0, Number(t.tendered) - Number(t.amount)) : 0), 0);
      return { saleId: payload.id, number: zhEn("離線-", "Offline-") + String(payload.id).slice(-4).toUpperCase(), total: ticketTotals(ticket.lines, ticket.orderDiscount).total, change, pointsEarned: 0, offline: true };
    };
    let res;
    if (typeof navigator !== "undefined" && navigator.onLine === false) res = queue();
    else {
      try { res = await checkoutSale(payload); }
      catch (e) { if (isNetworkError(e)) res = queue(); else throw e; }
    }
    // Offline, a booking only shows 已付 once its sale syncs (the server
    // records the payment); nothing is marked paid locally.
    // Bring the app's copies up to date: appointments, balances, stock.
    const aptIds = ticket.lines.filter(l => l.kind === "appointment").map(l => l.aptId);
    if (!res.offline) try {
      if (aptIds.length) {
        const fresh = await fetchAppointmentsByIds(aptIds);
        const byId = Object.fromEntries(fresh.map(a => [a.id, a]));
        setState(s => ({ ...s, appointments: s.appointments.map(a => byId[a.id] ? { ...a, ...byId[a.id] } : a) }));
      }
      if (ticket.customerId) {
        const m = await fetchMembership(ticket.customerId, tid);
        if (m) setState(s => ({
          ...s,
          users: s.users[ticket.customerId] ? { ...s.users, [ticket.customerId]: { ...s.users[ticket.customerId], wallet: Number(m.wallet) || 0, points: Number(m.points) || 0 } } : s.users,
          memberships: (s.memberships || []).map(x => x.userId === ticket.customerId && x.tenantId === tid ? { ...x, wallet: Number(m.wallet) || 0, points: Number(m.points) || 0 } : x),
        }));
      }
    } catch { /* the sale is recorded; the next sync catches up */ }
    if (!res.duplicate) {
      const sold = {};
      for (const l of ticket.lines) if (l.kind === "product") sold[l.productId] = (sold[l.productId] || 0) + Number(l.qty);
      setProducts(ps => (ps || []).map(p => sold[p.id] && p.trackStock ? { ...p, stock: p.stock - sold[p.id] } : p));
    }
    if (ticket.heldId) setHeld(hs => hs.filter(h => h.id !== ticket.heldId));
    const totals = ticketTotals(ticket.lines, ticket.orderDiscount);
    return {
      ...res, total: Number(res.total), change: Number(res.change) || 0,
      receipt: {
        number: res.number, kind: "sale", at: new Date().toISOString(), customerName: customerById(ticket.customerId)?.name,
        lines: ticket.lines.map(l => ({ name: l.kind === "wallet_topup" ? zhEn("錢包儲值", "Wallet top-up") + (l.bonus ? zhEn("（送 ", " (bonus ") + money(l.bonus, sym) + zhEn("）", ")") : "") : l.name, qty: l.kind === "product" || l.kind === "custom" ? l.qty : 1, unit: l.kind === "product" || l.kind === "custom" ? l.unitPrice : lineTotal(l), discount: Number(l.discount) || 0, total: lineTotal(l), original: l.listPrice ?? null, promoName: l.promoName || null })),
        orderDiscount: totals.orderDiscount, total: totals.total,
        payments: tenders.map(t => ({ name: t.methodName, amount: t.amount, tendered: t.tendered ?? null, points: t.points })),
        change: Number(res.change) || 0, pointsEarned: res.pointsEarned || 0,
      },
    };
  };

  // ── scanners: Bluetooth / USB (keyboard) scanners + the camera ──
  const registerScan = useRef(null);
  const scannerRef = useRef(null);
  scannerRef.current = scanner;
  const openScanner = useCallback((cb, title = zhEn("掃描條碼", "Scan barcode"), continuous = true) => setScanner({ cb, title, continuous, status: "" }), []);
  registerScan.open = () => openScanner((code) => {
    const ok = registerScan.current?.(code);
    return ok;
  }, zhEn("掃描加入購物車", "Scan to add to cart"));
  const handleCode = useCallback((code) => {
    const s = scannerRef.current;
    if (s?.cb) {
      const r = s.cb(code);
      if (r === "close") { setScanner(null); return; }
      setScanner(x => x && { ...x, status: r ? "✓ " + code : zhEn("搵唔到 ", "Not found: ") + code });
      if (!r) beep(false);
      return;
    }
    if (tab === "register") {
      const ok = registerScan.current?.(code);
      if (!ok) { beep(false); toast?.(zhEn("搵唔到條碼 ", "Barcode not found: ") + code, "error"); } else beep(true);
    }
  }, [tab, toast]);
  useEffect(() => startBarcodeListener((code) => handleCode(code)), [handleCode]);

  if (!tenant.posEnabled) {
    return <Empty icon="🛍️" title={zhEn("收銀 POS — 付費方案功能", "POS — a paid-plan feature")} sub={zhEn("基本版起包含收銀：收款、收據、商品（基本版 100 件 · 專業版 500 件 + 庫存 / 退款 / 交更 · 旗艦版無上限 + 採購 / AI）。", "Every paid plan includes the POS: checkout, receipts and products (Basic 100 · Pro 500 + stock / refunds / shifts · Enterprise unlimited + purchasing / AI).")}
      action={onUpgrade && <button type="button" onClick={onUpgrade} style={{ border: "none", borderRadius: 99, padding: "11px 22px", background: C.grad, color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>{zhEn("睇方案 · 升級", "See plans · Upgrade")}</button>} />;
  }
  const tabNeed = POS_TAB_NEED[tab];
  const tabLocked = tabNeed && !has(tabNeed);

  const body = products === null
    ? <p style={{ textAlign: "center", color: C.sub, fontWeight: 800, padding: 40 }}>{zhEn("載入中…", "Loading…")}</p>
    : (
      <>
        {loadErr && <p style={{ color: C.red, fontWeight: 800, fontSize: 13 }}>{zhEn("載入失敗：", "Failed to load: ")}{loadErr} <button type="button" onClick={load} style={{ border: "none", background: "none", color: C.accent, fontWeight: 900, cursor: "pointer" }}>{zhEn("再試", "Retry")}</button></p>}
        {(!online || outbox.length > 0) && (() => {
          const failed = outbox.filter(e => e.error).length;
          const tone = failed ? ["#FEF2F2", "#B91C1C", "#FECACA"] : !online ? ["#FFF7ED", "#9A3412", "#FED7AA"] : ["#EFF6FF", "#1D4ED8", "#BFDBFE"];
          return (
            <button type="button" onClick={() => setOutboxOpen(true)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, border: "1.5px solid " + tone[2], background: tone[0], borderRadius: 16, padding: "10px 14px", marginBottom: 12, fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
              <span style={{ fontSize: 20 }}>{failed ? "⚠️" : !online ? "📴" : "⏳"}</span>
              <span style={{ flex: 1, fontWeight: 800, color: tone[1], fontSize: 13.5, lineHeight: 1.5 }}>
                {failed ? failed + zhEn(" 張離線單上傳唔到 — 撳入去睇", " offline sales couldn't be uploaded — tap to see")
                  : !online ? zhEn("離線模式：照收現金、信用卡機等，連返網會自動上傳", "Offline mode: keep taking cash, card terminals and so on — they upload automatically when you're back online") + (outbox.length ? zhEn("（", " (") + outbox.length + zhEn(" 張等緊）", " waiting)") : "") + zhEn("。錢包、積分、儲值要等連返網。", ". Wallet, points and top-ups need a connection.")
                  : outbox.length + zhEn(" 張離線單上傳緊…", " offline sales uploading…")}
              </span>
              <span style={{ fontWeight: 900, color: tone[1], fontSize: 13 }}>›</span>
            </button>
          );
        })()}
        {tabLocked && <Empty icon="🔒" title={zhEn("呢個功能要" + posTierPlan(tabNeed) + "先用得到", "This needs the " + posTierPlan(tabNeed) + " plan")} sub={{ inventory: zhEn("追蹤庫存、盤點、低存量提示", "Stock tracking, stock counts, low-stock alerts"), promotions: zhEn("買一送一、第二件半價、會員優惠", "Buy-one-get-one, second-item discounts, member deals"), shift: zhEn("開更備用金、交更點錢、Z 報表", "Opening float, end-of-shift cash count, Z report"), purchasing: zhEn("供應商、採購單、收貨入庫、退貨", "Suppliers, purchase orders, receiving, returns") }[tab]}
          action={onUpgrade && <button type="button" onClick={onUpgrade} style={{ border: "none", borderRadius: 99, padding: "11px 22px", background: C.grad, color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>{zhEn("升級" + posTierPlan(tabNeed), "Upgrade to " + posTierPlan(tabNeed))}</button>} />}
        {tab === "register" && shiftOpen === null && has("standard") && (
          <button type="button" onClick={() => setTab("shift")} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, border: "1.5px dashed " + C.amber, background: C.amberSoft, borderRadius: 16, padding: "10px 14px", marginBottom: 12, fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
            <span style={{ fontSize: 20 }}>🗄️</span>
            <span style={{ flex: 1, fontWeight: 800, color: "#92400E", fontSize: 13.5 }}>{zhEn("未開更 — 開更記低備用金，收工就可以對數", "No shift open — open one to record the float and balance up at closing time")}</span>
            <span style={{ fontWeight: 900, color: "#92400E", fontSize: 13 }}>{zhEn("開更 ›", "Open shift ›")}</span>
          </button>
        )}
        {tab === "register" && (
          <Register offline={!online} aptById={aptById} onAptPrice={setAptPrice} aptMath={helpers}
            isBoss={isBoss} discountCap={isBoss ? 100 : (user.permissions?.discountCapPercent ?? 0)} tenant={tenant} sym={sym} products={products} held={held} ticket={ticket} setTicket={setTicket}
            customers={customers} customerById={customerById} aptsFor={aptsFor} todayUnpaid={todayUnpaid} aptDue={aptDue} petOf={petOf}
            spas={spas} addons={addons} priceSpa={priceSpa} priceAddon={priceAddon} onAddToApt={onAddToApt} walletBonusFor={helpers.walletBonusFor}
            onHeldQty={heldQty} onScan={registerScan} can={can} has={has} toast={toast} promotions={promotions} categories={categories}
            onPay={(totals) => { if (totals.total < 0) return; setPayTotals(totals); }}
            onHoldSave={async (h) => {
              try { const row = await saveHeldTicket({ ...h, tenantId: tid }); setHeld(hs => [row, ...hs.filter(x => x.id !== row.id)]); toast?.(zhEn("已掛單", "Ticket held")); }
              catch (e) { toast?.(zhEn("掛單失敗：", "Couldn't hold: ") + (e?.message || e), "error"); throw e; }
            }}
            onHoldDelete={async (id) => { try { await deleteHeldTicket(id); setHeld(hs => hs.filter(h => h.id !== id)); } catch (e) { toast?.(zhEn("刪除失敗", "Delete failed"), "error"); } }} />
        )}
        {tab === "history" && <History can={can} has={has} tenant={tenant} sym={sym} customerById={customerById} products={products} methods={methods} refreshKey={refreshKey}
          onScanOpen={(cb) => openScanner(cb, zhEn("掃描換貨商品", "Scan exchange item"))}
          onChanged={() => { load(); setRefreshKey(k => k + 1); }} />}
        {tab === "products" && (
          <div className="pos-noscroll" style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {[["list", zhEn("商品", "Products")], ["category", zhEn("分類", "Categories")], ["brand", zhEn("品牌", "Brands")]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setProductsView(k)} style={{ border: "none", borderRadius: 99, padding: "7px 14px", fontFamily: "inherit", fontWeight: 900, fontSize: 13, cursor: "pointer", background: productsView === k ? C.accentSoft : "transparent", color: productsView === k ? C.accent : C.sub }}>{l}</button>
            ))}
          </div>
        )}
        {tab === "products" && productsView !== "list" && (
          <Taxonomy key={productsView} tenant={tenant} kind={productsView} items={productsView === "brand" ? brands : categories}
            setItems={productsView === "brand" ? setBrands : setCategories} products={products} toast={toast}
            onProductsChanged={() => fetchPosProducts(tid).then(setProducts).catch(() => {})} />
        )}
        {tab === "products" && productsView === "list" && <Products has={has} canDelete={can("deleteProduct")} maxProducts={maxProducts} productCount={planInfo?.products} onCountChanged={reloadPlanInfo} onTaxonomyChanged={reloadTaxonomy} categories={categories} setCategories={setCategories} brands={brands} setBrands={setBrands} suppliers={suppliers} tenant={tenant} sym={sym} products={products} setProducts={(fn) => setProducts(ps => typeof fn === "function" ? fn(ps || []) : fn)} heldQty={(pid) => heldQty(pid)} onScanOpen={openScanner}
          onOpenStock={(pid) => { setStockFocus(pid); setTab("inventory"); }} />}
        {tab === "inventory" && !tabLocked && <Inventory tenant={tenant} sym={sym} products={products} setProducts={(fn) => setProducts(ps => typeof fn === "function" ? fn(ps || []) : fn)} heldQty={(pid) => heldQty(pid)} onScanOpen={openScanner}
          focusId={stockFocus} clearFocus={() => setStockFocus(null)} />}
        {tab === "reports" && can("reports") && <Reports has={has} tenant={tenant} sym={sym} customerById={customerById} refreshKey={refreshKey} products={products} />}
        {tab === "purchasing" && !tabLocked && <Purchasing tenant={tenant} sym={sym} products={products} setProducts={(fn) => setProducts(ps => typeof fn === "function" ? fn(ps || []) : fn)}
          onScanOpen={openScanner} toast={toast} onStockChanged={load} />}
        {tab === "promotions" && !tabLocked && <Promotions tenant={tenant} sym={sym} products={products} toast={toast} readOnly={!can("discount")} onChange={reloadPromotions} />}
        {tab === "shift" && !tabLocked && <Shift tenant={tenant} sym={sym} user={user} can={can} onShiftChange={setShiftOpen} />}
        {tab === "settings" && (isBoss ? <PosSettings tenant={tenant} setState={setState} sym={sym} has={has} tier={tier} maxProducts={maxProducts} productCount={planInfo?.products} /> : <Empty icon="🔒" title={zhEn("只有店主可以改設定", "Only the owner can change settings")} />)}
      </>
    );

  return (
    <div style={{ fontFamily: "inherit", paddingBottom: 90 }}>
      <PosStyles />
      <div className="pos-noscroll" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 14, position: "sticky", top: 0, zIndex: 20, background: C.bg, padding: "6px 0" }}>
        {TABS.filter(([k]) => k !== "reports" || can("reports")).map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)} style={{
            flexShrink: 0, display: "flex", alignItems: "center", gap: 6, border: "none", borderRadius: 99, padding: "9px 14px", cursor: "pointer", fontFamily: "inherit",
            fontWeight: 900, fontSize: 14, background: tab === k ? C.ink : "#fff", color: tab === k ? "#fff" : C.ink,
            boxShadow: tab === k ? "0 6px 16px rgba(74,74,104,0.25)" : "0 2px 8px rgba(74,74,104,0.06)",
          }}>
            <Icon size={16} strokeWidth={2.5} />{label}{POS_TAB_NEED[k] && !has(POS_TAB_NEED[k]) ? " 🔒" : ""}
            {k === "register" && ticket.lines.length > 0 && tab !== k && <span style={{ background: C.grad, color: "#fff", borderRadius: 99, padding: "0 6px", fontSize: 11 }}>{ticket.lines.length}</span>}
          </button>
        ))}
      </div>
      {body}

      {payTotals && (
        <PaySheet offline={!online} has={has} open totals={payTotals} customer={customerById(ticket.customerId)} tenant={tenant} methods={methods} sym={sym}
          onClose={() => setPayTotals(null)}
          onSubmit={submitSale}
          onPromoStale={reloadPromotions}
          onPrint={(res) => printReceipt(res.receipt, { tenant, sym })}
          onDone={() => { setPayTotals(null); setTicket(null); setRefreshKey(k => k + 1); reloadPromotions(); }} />
      )}

      {outboxOpen && (
        <Sheet open onClose={() => setOutboxOpen(false)} title={zhEn("離線單", "Offline sales")}>
          <p style={{ fontSize: 13, fontWeight: 700, color: C.sub, margin: "0 2px 12px", lineHeight: 1.6 }}>
            {online ? zhEn("已經連返網。", "You're back online. ") : zhEn("而家冇網絡。", "No connection. ")}{zhEn("離線收嘅單會保留喺呢部機，連返網自動上傳，時間會記返收款嗰陣。唔好清除瀏覽器資料，直至全部上傳完。", "Sales taken offline stay on this device and upload automatically when you're back online, keeping the time of the sale. Don't clear browser data until they have all uploaded.")}
          </p>
          {outbox.length === 0 ? <Empty icon="✅" title={zhEn("全部已經上傳", "Everything has been uploaded")} /> : outbox.map(e => (
            <Card key={e.saleId} style={{ padding: 12, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 900, color: C.ink }}>{zhEn("離線-", "Offline-")}{String(e.saleId).slice(-4).toUpperCase()} · {money(e.total || 0, sym)}</p>
                  <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: C.sub }}>{new Date(e.payload?.offlineAt || e.queuedAt).toLocaleString(dateLocale())}{e.customerName ? " · " + e.customerName : ""}</p>
                  {e.error && <p style={{ margin: "4px 0 0", fontSize: 12.5, fontWeight: 800, color: C.red }}>{zhEn("上傳唔到：", "Couldn't upload: ")}{posErrorText({ message: e.error })}</p>}
                </div>
                <span style={{ fontSize: 12, fontWeight: 900, color: e.error ? C.red : C.amber, whiteSpace: "nowrap" }}>{e.error ? zhEn("要處理", "Needs attention") : zhEn("等緊", "Waiting")}</span>
              </div>
              {e.error && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button type="button" onClick={() => { setOutbox(outboxRetry(tid, e.saleId)); runSync(); }} style={{ flex: 1, border: "none", borderRadius: 12, padding: "9px", background: C.grad, color: "#fff", fontWeight: 900, cursor: "pointer", fontFamily: "inherit" }}>{zhEn("再試", "Retry")}</button>
                  <button type="button" onClick={() => { if (window.confirm(zhEn("刪除呢張離線單？呢筆錢唔會記入系統，要自己手動補記。", "Delete this offline sale? It will not be recorded in the system — you'll have to record it yourself."))) setOutbox(outboxRemove(tid, e.saleId)); }} style={{ flex: 1, border: "1.5px solid " + C.line, borderRadius: 12, padding: "9px", background: "#fff", color: C.red, fontWeight: 900, cursor: "pointer", fontFamily: "inherit" }}>{zhEn("刪除", "Delete")}</button>
                </div>
              )}
            </Card>
          ))}
          {outbox.length > 0 && online && <button type="button" onClick={runSync} style={{ width: "100%", border: "none", borderRadius: 14, padding: "12px", background: C.ink, color: "#fff", fontWeight: 900, cursor: "pointer", fontFamily: "inherit", marginTop: 4 }}>{zhEn("而家上傳", "Upload now")}</button>}
        </Sheet>
      )}

      <CameraScanner open={!!scanner} title={scanner?.title} continuous={scanner?.continuous !== false} status={scanner?.status}
        onClose={() => setScanner(null)} onCode={handleCode} />
    </div>
  );
}

// 設定 — receipt + the points-earn rules (shared with 積分 settings).
function PosSettings({ tenant, setState, sym, has, tier, maxProducts, productCount }) {
  const ps = tenant.posSettings || {};
  const patch = (p) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === tenant.id ? { ...t, posSettings: { ...(t.posSettings || {}), ...p } } : t) }));
  return (
    <div style={{ maxWidth: 640 }}>
      <Card style={{ padding: 16, marginBottom: 12 }}>
        <Label>{zhEn("你嘅收銀方案", "Your POS plan")}</Label>
        <p style={{ fontSize: 15, fontWeight: 900, color: C.ink, margin: "0 0 4px" }}>{posTierPlan(tier) || "—"} · {zhEn("商品 ", "Products ")}{productCount ?? "—"}{maxProducts ? " / " + maxProducts : zhEn("（無上限）", " (unlimited)")}</p>
        <p style={{ fontSize: 12.5, fontWeight: 700, color: C.sub, margin: 0, lineHeight: 1.6 }}>
          {tier === "lite" ? zhEn("升級專業版：庫存、款式、退款換貨、交更、優惠、錢包、500 件商品。", "Upgrade to Pro: stock, variants, refunds & exchanges, shifts, promotions, wallet, 500 products.")
            : tier === "standard" ? zhEn("升級旗艦版：採購 / 供應商、AI 新增商品、批量匯入、毛利同分類報表、積分抵扣、商品無上限。", "Upgrade to Enterprise: purchasing / suppliers, AI product setup, bulk import, margin & category reports, points payments, unlimited products.")
            : zhEn("已包含全部收銀功能。", "Includes every POS feature.")}
        </p>
      </Card>
      <PayMethodsCard tenant={tenant} setState={setState} />
      <Card style={{ padding: 16, marginBottom: 12 }}>
        <Label>{zhEn("收據", "Receipt")}</Label>
        <Field label={zhEn("單號前綴", "Receipt prefix")} value={ps.receiptPrefix || ""} onChange={e => patch({ receiptPrefix: e.target.value.slice(0, 8) })} placeholder={zhEn("例如 PB-", "e.g. PB-")} hint={zhEn("收據會顯示 ", "Receipts will show ") + (ps.receiptPrefix || "") + "123"} />
        <Field label={zhEn("收據底部字句", "Receipt footer text")} value={ps.receiptFooter || ""} onChange={e => patch({ receiptFooter: e.target.value.slice(0, 120) })} placeholder={zhEn("例如 7 日內憑收據換貨", "e.g. Exchanges within 7 days with receipt")} />
      </Card>
      <Card style={{ padding: 16, marginBottom: 12 }}>
        <PointsEarnSettings tenant={tenant} setState={setState} sym={sym} />
      </Card>
      {has("standard") && <Card style={{ padding: 16, marginBottom: 12 }}>
        <Label>{zhEn("員工權限（店主永遠可以）", "Staff permissions (owners can always)")}</Label>
        {[["refund", zhEn("退款 / 退貨 / 換貨", "Refunds / returns / exchanges"), false], ["discount", zhEn("折扣（項目 % 同整單折扣）", "Discounts (line % and order discount)"), true], ["priceOverride", zhEn("手動改價", "Manual price change"), false], ["closeShift", zhEn("交更點錢", "Close the shift (cash count)"), true]].map(([k, label, def]) => (
          <ToggleRow key={k} label={zhEn("員工可以", "Staff allowed: ") + label} on={(ps.staffCan?.[k]) ?? def}
            onChange={v => patch({ staffCan: { ...(ps.staffCan || {}), [k]: v } })} />
        ))}
        <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "8px 2px 0" }}>{zhEn("系統喺伺服器都會檢查，員工就算繞過畫面都做唔到。", "The server checks this too, so staff can't get around it from the screen.")}</p>
      </Card>}
      <Card style={{ padding: 16 }}>
        <Label>{zhEn("硬件", "Hardware")}</Label>
        <p style={{ fontSize: 13, fontWeight: 700, color: C.ink, lineHeight: 1.6, margin: 0 }}>
          {zhEn("📷 相機掃描：iPhone / iPad / Android 都用得，撳 掃描 掣就得。", "📷 Camera scanning: works on iPhone, iPad and Android — tap Scan.")}<br />
          {zhEn("🔫 藍牙 / USB 掃描槍：配對後喺收銀頁直接掃，唔使撳任何掣。", "🔫 Bluetooth / USB barcode scanner: once paired, just scan on the POS screen.")}<br />
          {zhEn("🧾 收據：用系統列印（AirPrint）。熱敏收據機會喺下一階段加入。", "🧾 Receipts: printed through the system (AirPrint). Thermal receipt printers come in a later phase.")}
        </p>
      </Card>
    </div>
  );
}

// 收款方式 at the till. Same list as 設定 → 收款方式 (tenant.depositMethods);
// a row shows at the till when forCheckout is on. Tapping a chip adds the
// preset, or switches an existing row's 店內結帳 on / off.
const COUNTER_PRESETS = [
  { key: "card", name: zhEn("💳 信用卡 / 信用卡機", "💳 Card / card terminal") }, { key: "fps", name: zhEn("FPS（轉數快）", "FPS (Faster Payment System)") }, { key: "octopus", name: zhEn("🐙 八達通", "🐙 Octopus") },
  { key: "payme", name: "PayMe" }, { key: "eps", name: "EPS" }, { key: "wechat", name: zhEn("WeChat Pay 微信支付", "WeChat Pay") }, { key: "alipay", name: zhEn("Alipay 支付寶", "Alipay") },
];
const newId4 = () => (crypto?.randomUUID ? crypto.randomUUID() : "m" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8));
function PayMethodsCard({ tenant, setState }) {
  const methods = Array.isArray(tenant.depositMethods) ? tenant.depositMethods : [];
  const save = (next) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === tenant.id ? { ...t, depositMethods: next } : t) }));
  const rowOf = (key, name) => methods.find(m => m.presetKey === key) || methods.find(m => m.name === name);
  const onAtTill = (m) => !!m && m.enabled !== false && m.forCheckout !== false;
  const toggle = (pr) => {
    const m = rowOf(pr.key, pr.name);
    if (!m) return save([...methods, { id: newId4(), presetKey: pr.key, name: pr.name, qrUrl: "", instructions: "", enabled: true, forCustomer: false, forCheckout: true }]);
    const on = !onAtTill(m);
    save(methods.map(x => x.id === m.id ? { ...x, enabled: on ? true : x.enabled, forCheckout: on, ...(on || x.forCustomer !== false ? {} : { enabled: false }) } : x));
  };
  const extra = methods.filter(m => onAtTill(m) && !COUNTER_PRESETS.some(p => p.key === m.presetKey || p.name === m.name) && m.presetKey !== "cash" && !String(m.name || "").includes("現金"));
  return (
    <Card style={{ padding: 16, marginBottom: 12 }}>
      <Label>{zhEn("收款方式（收銀台）", "Payment methods (till)")}</Label>
      <p style={{ fontSize: 12.5, fontWeight: 700, color: C.sub, margin: "0 0 10px", lineHeight: 1.6 }}>{zhEn("現金同錢包一定有。撳下面加埋信用卡機、轉數快等，收款時就會見到。", "Cash and wallet are always available. Tap below to add card terminals, FPS and so on — they appear at checkout.")}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <span style={{ borderRadius: 99, padding: "8px 14px", fontWeight: 900, fontSize: 13.5, background: C.greenSoft || "#ECFDF5", color: C.green }}>{zhEn("✓ 💵 現金", "✓ 💵 Cash")}</span>
        {COUNTER_PRESETS.map(pr => {
          const on = onAtTill(rowOf(pr.key, pr.name));
          return <button key={pr.key} type="button" onClick={() => toggle(pr)} style={{ border: "1.5px solid " + (on ? C.green : C.line), background: on ? "#ECFDF5" : "#fff", color: on ? C.green : C.ink, borderRadius: 99, padding: "8px 14px", fontWeight: 900, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>{on ? "✓ " : "＋ "}{pr.name}</button>;
        })}
        {extra.map(m => <span key={m.id} style={{ borderRadius: 99, padding: "8px 14px", fontWeight: 900, fontSize: 13.5, background: "#ECFDF5", color: C.green }}>✓ {m.name}</span>)}
      </div>
      <p style={{ fontSize: 12, fontWeight: 700, color: C.faint, margin: "10px 2px 0" }}>{zhEn("更多設定（QR、客戶線上付款）：設定 → 收款方式", "More settings (QR, customer online payment): Settings → Payment methods")}</p>
    </Card>
  );
}

// Points-earn rules — the shop decides. Defaults keep today's behaviour:
// grooming earns on the full bill, retail earns only on the cash/card part.
export function PointsEarnSettings({ tenant, setState, sym = "$", compact }) {
  const ps = tenant.posSettings || {};
  const patch = (p) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === tenant.id ? { ...t, posSettings: { ...(t.posSettings || {}), ...p } } : t) }));
  const earnPer = ps.retailEarnPer || tenant.loyaltyEarnPer || 100;
  return (
    <div>
      {!compact && <Label>{zhEn("積分賺取規則", "Points earning rules")}</Label>}
      {!tenant.loyaltyEnabled && <p style={{ fontSize: 12, fontWeight: 800, color: C.amber, margin: "0 0 8px" }}>{zhEn("積分未啟用 — 喺 設定 → 積分 開啟後先會生效。", "Points are off — they take effect once switched on in Settings → Points.")}</p>}
      <ToggleRow label={zhEn("美容：用錢包 / 積分付嘅部分都賺積分", "Grooming: earn points on the part paid by wallet / points too")} sub={zhEn("開（預設）：成張單都計分。關：只計現金 / 卡 / 轉數快等付款嘅部分。", "On (default): the whole ticket earns points. Off: only the part paid by cash / card / FPS earns points.")}
        on={ps.aptEarnOnStoredValue !== false} onChange={v => patch({ aptEarnOnStoredValue: v })} />
      {tenant.posEnabled && (
        <>
          <ToggleRow label={zhEn("零售（收銀）都賺積分", "Retail (POS) earns points too")} on={ps.retailEarnsPoints !== false} onChange={v => patch({ retailEarnsPoints: v })} />
          {ps.retailEarnsPoints !== false && (
            <>
              <ToggleRow label={zhEn("零售：用錢包 / 積分付嘅部分都賺積分", "Retail: earn points on the part paid by wallet / points too")} sub={zhEn("關（預設）：只計現金 / 卡等付款嘅部分，避免儲值再賺分。", "Off (default): only the part paid by cash / card earns points, so top-ups don't earn again.")}
                on={!!ps.retailEarnOnStoredValue} onChange={v => patch({ retailEarnOnStoredValue: v })} />
              <div style={{ marginTop: 10 }}>
                <Field label={zhEn("零售每消費幾多錢得 1 分（", "Retail spend per 1 point (") + sym + zhEn("）", ")")} inputMode="numeric" value={String(ps.retailEarnPer ?? "")} placeholder={String(tenant.loyaltyEarnPer || 100)}
                  onChange={e => { const v = e.target.value.replace(/[^\d]/g, ""); patch({ retailEarnPer: v ? Math.max(1, Number(v)) : null }); }}
                  hint={zhEn("而家：每 ", "Now: every ") + sym + earnPer + zhEn(" = 1 分（留空 = 同美容一樣）", " = 1 point (leave empty = same as grooming)")} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
